import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Search, Clock, Users, ArrowLeft, UtensilsCrossed } from "lucide-react";
import { LogoMark } from "@/components/dw/Logo";

const CATEGORIES = ["All", "Breakfast", "Lunch", "Dinner", "Snack", "Soup", "Drink", "Side"];

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  useEffect(() => {
    fetch(`${API}/recipes`).then((r) => r.json()).then((d) => { setRecipes(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return recipes.filter((r) => {
      if (cat !== "All" && r.category !== cat) return false;
      if (q) {
        const s = q.toLowerCase();
        return (
          r.name.toLowerCase().includes(s) ||
          (r.tags || []).some((t) => t.toLowerCase().includes(s)) ||
          (r.ingredients || []).some((i) => i.toLowerCase().includes(s))
        );
      }
      return true;
    });
  }, [recipes, q, cat]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <header className="border-b" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark size={36} />
            <span className="font-display text-xl" style={{ color: "var(--primary)" }}>DietWalaa</span>
          </Link>
          <Link to="/" className="text-xs font-bold flex items-center gap-1" style={{ color: "var(--primary)" }}>
            <ArrowLeft size={12} /> Back to home
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 pt-10 pb-6">
        <div className="section-eyebrow">Dietwalaa Kitchen</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: "var(--primary)" }}>
          Recipes
        </h1>
        <p className="mt-3 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
          Real Indian home-cook recipes — the exact dishes we use in our Dietwalaa diet plans. Original recipes, written and curated by Dt Manthan Sharma.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sticky top-0 z-20 py-3 border-y" style={{ background: "var(--bg)", borderColor: "var(--border-subtle)" }}>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-secondary)" }} />
            <input
              data-testid="recipe-search"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, tag or ingredient…"
              className="w-full h-10 pl-9 pr-3 rounded-full border-2 text-sm font-medium outline-none"
              style={{ borderColor: "var(--border-strong)", color: "var(--primary)" }}
            />
          </div>
        </div>
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {CATEGORIES.map((c) => {
            const active = cat === c;
            return (
              <button
                key={c}
                data-testid={`cat-${c.toLowerCase()}`}
                onClick={() => setCat(c)}
                className="text-xs font-bold px-3 py-1.5 rounded-full border-2 whitespace-nowrap"
                style={{
                  background: active ? "var(--primary)" : "var(--bg)",
                  color: active ? "var(--bg)" : "var(--primary)",
                  borderColor: active ? "var(--primary)" : "var(--border-subtle)",
                }}
              >{c}</button>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-8">
        {loading && <div className="text-center text-sm" style={{ color: "var(--text-secondary)" }}>Loading recipes…</div>}
        {!loading && filtered.length === 0 && (
          <div className="card-soft p-8 text-center">
            <UtensilsCrossed size={28} className="mx-auto" style={{ color: "var(--red)" }} />
            <p className="mt-3 font-bold" style={{ color: "var(--primary)" }}>No recipes match that filter</p>
            <p className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>Try a different category or search term.</p>
          </div>
        )}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((r) => (
            <Link
              key={r.slug}
              to={`/recipes/${r.slug}`}
              data-testid={`recipe-card-${r.slug}`}
              className="card-soft p-5 group hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-xl leading-tight" style={{ color: "var(--primary)" }}>{r.name}</h3>
                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full flex-shrink-0" style={{ background: "var(--yellow)", color: "var(--primary)" }}>{r.category}</span>
              </div>
              <div className="mt-3 flex items-center gap-3 text-xs" style={{ color: "var(--text-secondary)" }}>
                {(r.prep_time_min + r.cook_time_min) > 0 && (
                  <span className="flex items-center gap-1"><Clock size={11} /> {r.prep_time_min + r.cook_time_min} min</span>
                )}
                {r.servings > 0 && (
                  <span className="flex items-center gap-1"><Users size={11} /> {r.servings} serves</span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(r.tags || []).slice(0, 3).map((t, i) => (
                  <span key={i} className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--bg-paper)", color: "var(--primary)" }}>{t}</span>
                ))}
              </div>
              <div className="mt-4 text-xs font-bold" style={{ color: "var(--red)" }}>Read recipe →</div>
            </Link>
          ))}
        </div>
      </section>

      <footer className="border-t mt-8" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="max-w-6xl mx-auto px-4 py-6 text-xs text-center" style={{ color: "var(--text-secondary)" }}>
          All recipes authored by Dt Manthan Sharma · © Dietwalaa
        </div>
      </footer>
    </div>
  );
}
